import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Box, Button, CircularProgress, LinearProgress, Typography} from '@mui/material';
import axios, {AxiosInstance, AxiosResponse} from 'axios';
import ReactCrop, {Crop} from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import {useTranslation} from 'react-i18next';
import Asset from '../../../models/Asset';
import {AiPromptVariant} from '../../../models/Template';
import {
    AiEditConflictCode,
    AiEditErrorCode,
    AiEditJobStatus,
    pollAiEditUntilComplete,
} from '../../../services/jobService';
import {PresignedUploadResponse, uploadFileToS3} from '../../../utils/uploadUtils';
import {cropImageToBlob, cropOutputSize, MAX_UPLOAD_HEIGHT, PercentCrop} from '../FillMode/cropUtils';
import SparkleIcon from './SparkleIcon';

const FULL_CROP: PercentCrop = {unit: '%', x: 0, y: 0, width: 100, height: 100};

/** Clamp a percent crop into the image bounds (0..100, and x+w / y+h <= 100)
 *  so a stray drag / uninitialised box can't drive drawImage out of bounds. */
const clampPercentCrop = (c: PercentCrop): PercentCrop => {
    const width = Math.max(0, Math.min(c.width, 100));
    const height = Math.max(0, Math.min(c.height, 100));
    const x = Math.max(0, Math.min(c.x, 100 - width));
    const y = Math.max(0, Math.min(c.y, 100 - height));
    return {unit: '%', x, y, width, height};
};

export interface AiMagicStageProps {
    /** Authenticated axios instance (apiService.api) — passed in so the stage
     *  works inside both the mobile sheet and the desktop modal. */
    api: AxiosInstance;
    templateId: string;
    inputKey: string;
    designKey?: string;
    /** The freshly uploaded photo. Absent only when resuming a job whose source
     *  asset isn't in hand — "Try again" is then unavailable (needs its id). */
    sourceAsset?: Asset;
    /** "Before" image to compare against when sourceAsset is missing. */
    sourcePreviewUrl?: string;
    /** Named style options for this field (names only). >1 shows a picker;
     *  the chosen one's id is sent as variantId. Empty/one = no picker. */
    aiPromptVariants?: AiPromptVariant[];
    /** The field frame. Its presence (with folderId) is what OFFERS the optional
     *  crop step on the AI result; it is NOT a crop aspect any more — the crop
     *  is free-form and starts at the whole image, because a replaced image is
     *  contained inside its box at render time rather than cover-cropped.
     *  Absent (resume path) → the result is accepted as-is, no crop step. */
    frameSize?: { width: number; height: number };
    /** Folder to upload the cropped result into. Required for the crop step. */
    folderId?: string;
    /** The field removes background: the AI result is a transparent cutout the
     *  backend pads to the field shape, so it drops in whole — the crop step is
     *  skipped and the padded result is accepted directly. */
    removeBackground?: boolean;
    /** Poll this job on mount instead of showing the idle button (resume path). */
    autoResumeJobId?: string;
    /** The user picked the AI result — apply it to the board. */
    onAccept: (asset: Asset) => void;
    /** The user is done with the AI stage; the plain photo is already applied. */
    onKeepOriginal: () => void;
}

type Phase = 'idle' | 'running' | 'preview' | 'error';

/** Which side of the before/after comparison is showing. */
type CompareSide = 'before' | 'after';

const PREVIEW_BOX = {
    width: '100%',
    height: '46vh',
    maxHeight: 380,
    borderRadius: '14px',
    backgroundColor: '#0e1f2c',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
};

const IMG_STYLE: React.CSSProperties = {
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
};

/** The "AI magic" step of an image upload: offer the edit, run it with staged
 *  progress, then compare the result against the original.
 *
 *  Deliberately layout-container-free (no Dialog/Drawer of its own) so the same
 *  component drops into Fill Mode's bottom-sheet dialog and the desktop modal. */
const AiMagicStage: React.FC<AiMagicStageProps> = ({
                                                       api,
                                                       templateId,
                                                       inputKey,
                                                       designKey,
                                                       sourceAsset,
                                                       sourcePreviewUrl,
                                                       aiPromptVariants,
                                                       frameSize,
                                                       folderId,
                                                       removeBackground,
                                                       autoResumeJobId,
                                                       onAccept,
                                                       onKeepOriginal,
                                                   }) => {
    const {t} = useTranslation();

    const variants = aiPromptVariants ?? [];
    const hasVariantPicker = variants.length > 1;

    const [phase, setPhase] = useState<Phase>(autoResumeJobId ? 'running' : 'idle');
    const [progress, setProgress] = useState(0);
    const [progressKey, setProgressKey] = useState('aiEdit.progress.generating');
    const [result, setResult] = useState<Asset | null>(null);
    const [compare, setCompare] = useState<CompareSide>('after');
    const [errorMessage, setErrorMessage] = useState('');
    /** Set when the job was refused up-front (quota/cap/not enabled) — retrying
     *  would just be refused again, so the retry button is hidden. */
    const [retryBlocked, setRetryBlocked] = useState(false);
    /** The cancel button only dismisses the UI; say so once it's been used. */
    const [cancelledNotice, setCancelledNotice] = useState(false);
    /** The style the user picked — defaults to the first, preserved across
     *  "Try again" so they can switch before regenerating. */
    const [selectedVariantId, setSelectedVariantId] = useState<string | undefined>(variants[0]?.id);
    // Crop-the-result state (only used when the crop step is available).
    const [crop, setCrop] = useState<Crop>(FULL_CROP as Crop);
    const [percentCrop, setPercentCrop] = useState<PercentCrop>(FULL_CROP);
    const [committing, setCommitting] = useState(false);
    const [commitError, setCommitError] = useState('');
    // Displayed progress. The server only reports coarse stages (generating=10,
    // removing_background=60, done=100) and can't know GPT's finish time, so the
    // bar would sit frozen for 30-60s. We ease a client-side "creep" toward a
    // ceiling while running, and snap up whenever a real server update is higher.
    const [displayProgress, setDisplayProgress] = useState(0);

    // Never let the displayed bar go backwards; snap it up to any real update.
    useEffect(() => {
        setDisplayProgress((p) => Math.max(p, progress));
    }, [progress]);
    // Ease toward a 90% ceiling while running so the bar keeps visibly moving
    // during the long, opaque generation. Completion (server progress -> 100)
    // snaps it to full via the effect above just before phase leaves 'running'.
    useEffect(() => {
        if (phase !== 'running') return;
        // Slow eased creep tuned for a ~30-60s generation: ~15s time-constant,
        // so it drifts up gradually rather than rushing the ceiling.
        const id = setInterval(() => {
            setDisplayProgress((p) => (p < 90 ? p + (90 - p) * 0.03 : p));
        }, 600);
        return () => clearInterval(id);
    }, [phase]);

    const abortRef = useRef<AbortController | null>(null);
    const mountedRef = useRef(true);
    // Bumped on every start/cancel/resume. A CREATE request whose token no
    // longer matches (the user cancelled, or a second start won the race)
    // must not drive the UI back into a running/preview state.
    const generationRef = useRef(0);
    // Synchronous "a run is active" flag. Refs update immediately, so a rapid
    // double-tap that both read a stale `phase==='idle'` still can't fire two
    // POSTs — the second start sees this already true and bails.
    const runActiveRef = useRef(false);

    const beforeSrc = sourceAsset?.highRes ?? sourcePreviewUrl ?? '';
    // On the resume path there is no source asset to re-submit, so "Try again"
    // can't work and Cancel must close the dialog rather than offer an idle
    // Start button that would immediately fail.
    const isResumeOnly = !sourceAsset;

    // The crop step needs a target aspect and a folder to upload into. It's also
    // SKIPPED when the field removes background: that result is a transparent
    // cutout the backend pads to the field shape, so it drops in whole and
    // "Use this" just accepts the padded result directly (the !canCrop path).
    const canCrop = !!(folderId && frameSize?.width && frameSize?.height && !removeBackground);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            abortRef.current?.abort();
        };
    }, []);

    // When a result arrives (and we can crop), start FREE-FORM at the WHOLE
    // image. The field's frame no longer crops the picture — a replaced image is
    // contained inside its box at render time (scene/imageFit.ts) — so
    // pre-selecting the frame aspect would throw away exactly the subject the
    // user asked the model for. Re-runs per new result (e.g. after "Try again")
    // so one run's crop never carries into the next.
    useEffect(() => {
        if (!result || !canCrop) return;
        setPercentCrop(FULL_CROP);
        setCrop(FULL_CROP as Crop);
    }, [result, canCrop]);

    const failWith = useCallback((message: string, blocked = false) => {
        runActiveRef.current = false;
        if (!mountedRef.current) return;
        setErrorMessage(message);
        setRetryBlocked(blocked);
        setPhase('error');
    }, []);

    /** Poll an existing job to its terminal state and route to preview/error.
     *  `generation` is the token captured when this run began; if it has since
     *  been superseded (cancel / new start) every state write is skipped. */
    const runJob = useCallback(async (jobId: string, generation: number) => {
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        setCancelledNotice(false);
        setProgress(0);
        // Don't yank the bar backwards: startJob may have already crept it past 8
        // while the create POST was in flight. The resume path starts at 0 → 8.
        setDisplayProgress((p) => Math.max(p, 8));
        setProgressKey('aiEdit.progress.generating');
        setPhase('running');

        const isCurrent = () =>
            mountedRef.current && !controller.signal.aborted && generationRef.current === generation;

        try {
            const status: AiEditJobStatus = await pollAiEditUntilComplete(
                api,
                jobId,
                (update) => {
                    if (!isCurrent()) return;
                    setProgress(update.progress ?? 0);
                    if (update.progressMessage) setProgressKey(update.progressMessage);
                },
                undefined,
                controller.signal
            );
            if (!isCurrent()) return;

            if (status.status === 'completed' && status.asset) {
                runActiveRef.current = false;
                setResult(status.asset);
                setCompare('after');
                setPhase('preview');
                return;
            }

            const code: AiEditErrorCode = status.errorCode ?? 'internal';
            failWith(t(`aiEdit.errors.${code}`));
        } catch (error) {
            if (!isCurrent()) return;
            if ((error as Error)?.name === 'AbortError') return;
            failWith(t('aiEdit.errors.internal'));
        }
    }, [api, failWith, t]);

    /** Create a job for the current source asset (first run and "Try again"). */
    const startJob = useCallback(async () => {
        if (!sourceAsset) {
            failWith(t('aiEdit.errors.internal'), true);
            return;
        }
        // Guard against a rapid double-tap firing two POSTs. A ref (not `phase`)
        // because two clicks in one tick can both read a stale idle phase.
        if (runActiveRef.current) return;
        runActiveRef.current = true;

        const generation = ++generationRef.current;
        setCancelledNotice(false);
        setPhase('running');
        setProgress(0);
        setDisplayProgress(8);
        setProgressKey('aiEdit.progress.generating');

        try {
            const response = await api.post<{ jobId: string }>('/ai-edit/jobs', {
                templateId,
                inputKey,
                sourceAssetId: sourceAsset.id,
                ...(designKey ? {designKey} : {}),
                ...(selectedVariantId ? {variantId: selectedVariantId} : {}),
            });
            // The user cancelled (or restarted) while the POST was in flight —
            // do not drag the UI back into a running state.
            if (!mountedRef.current || generationRef.current !== generation) return;
            await runJob(response.data.jobId, generation);
        } catch (error) {
            if (!mountedRef.current || generationRef.current !== generation) return;
            if (axios.isAxiosError(error) && error.response?.status === 409) {
                const data = error.response.data as { errorCode?: AiEditConflictCode; jobId?: string };
                // Someone (this tab or another) already has a job running for
                // this field — resume it instead of reporting a failure.
                if (data?.errorCode === 'job_active' && data.jobId) {
                    await runJob(data.jobId, generation);
                    return;
                }
                const code: AiEditConflictCode = data?.errorCode ?? 'not_enabled';
                failWith(t(`aiEdit.errors.${code}`), true);
                return;
            }
            failWith(t('aiEdit.errors.internal'));
        }
    }, [api, designKey, failWith, inputKey, runJob, selectedVariantId, sourceAsset, t, templateId]);

    // Resume path: a job was already in flight (or just finished) when this
    // stage mounted, so skip idle and go straight to polling.
    useEffect(() => {
        if (autoResumeJobId) runJob(autoResumeJobId, ++generationRef.current);
        // Run once per resumed job id.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoResumeJobId]);

    /** Cancel dismisses the UI — the server job keeps running and counts. On the
     *  resume path (no source asset) there is nothing to restart, so cancel
     *  closes the dialog rather than showing a dead Start button. */
    const handleCancel = () => {
        // Supersede any in-flight run/create so their late resolutions are ignored.
        generationRef.current++;
        runActiveRef.current = false;
        abortRef.current?.abort();
        abortRef.current = null;
        if (isResumeOnly) {
            onKeepOriginal();
            return;
        }
        setCancelledNotice(true);
        setPhase('idle');
    };

    /** "Use this": with a crop step, crop the AI result to the frame aspect and
     *  upload the cropped PNG as the final asset; otherwise accept as-is.
     *
     *  Everything stays PNG (image/png) through crop + upload so the bg-removed
     *  cutout's alpha survives — a JPEG re-encode would fill transparency black. */
    const handleUse = async () => {
        if (!result) return;
        if (!canCrop) {
            onAccept(result);
            return;
        }
        setCommitError('');
        setCommitting(true);
        try {
            // Load a fresh CORS image for the canvas crop so it isn't tainted.
            // Cache-bust so a display fetch cached WITHOUT CORS can't poison it.
            const img = new Image();
            img.crossOrigin = 'anonymous';
            await new Promise<void>((resolve, reject) => {
                img.onload = () => resolve();
                img.onerror = () => reject(new Error('AI result image load failed'));
                const sep = result.highRes.includes('?') ? '&' : '?';
                img.src = `${result.highRes}${sep}x-aicrop=1`;
            });

            const safeCrop = clampPercentCrop(percentCrop);
            const blob = await cropImageToBlob(img, safeCrop, 'image/png', MAX_UPLOAD_HEIGHT);
            const dims = cropOutputSize(img.naturalWidth, img.naturalHeight, safeCrop, MAX_UPLOAD_HEIGHT);
            const name = result.name || 'ai-edit';
            const croppedFile = new File([blob], `${name}.png`, {type: 'image/png'});

            const presignedRes: AxiosResponse<PresignedUploadResponse> = await api.post(
                `/folder/${folderId}/asset/upload`, {}, {headers: {'Content-Type': 'image/png'}}
            );
            const {presignedPost, url: assetUrl} = presignedRes.data;

            await new Promise<void>((resolve, reject) => {
                uploadFileToS3(presignedPost, croppedFile, () => {}, () => resolve(), (err) => reject(new Error(err)));
            });

            const putRes = await api.put<Asset>(`/folder/${folderId}/asset/upload`, {
                name,
                url: assetUrl,
                type: 'image/png',
                width: dims.width,
                height: dims.height,
            });

            // Apply the cropped asset, then best-effort delete the now-superseded
            // UNCROPPED result so it doesn't linger as folder junk (every retry
            // would otherwise leave one behind). Fire-and-forget — a failed
            // cleanup must never block the accept. Guarded to the crop path only;
            // the resume path returns the result itself (above) and must keep it.
            //
            // NOT gated on mountedRef: onAccept/api target the still-mounted PARENT
            // (host dialog), so even if a mid-commit host close unmounts THIS stage
            // we must still apply + clean up rather than orphan the cropped asset.
            onAccept(putRes.data);
            if (result.id) {
                api.delete(`/asset/${result.id}`).catch(() => {});
            }
        } catch (error) {
            if (!mountedRef.current) return;
            setCommitError(t('aiEdit.errors.internal'));
        } finally {
            if (mountedRef.current) setCommitting(false);
        }
    };

    /** "Try again": with multiple variants, return to idle so the user can pick
     *  a different style first; otherwise regenerate straight away. */
    const handleTryAgain = () => {
        if (hasVariantPicker) {
            setPhase('idle');
            return;
        }
        startJob();
    };

    const canRetry = !!sourceAsset;

    const keepOriginalButton = (
        <Button
            data-testid="ai-magic-keep-original"
            onClick={onKeepOriginal}
            // While the crop is uploading, closing would unmount mid-PUT and
            // orphan the cropped asset — block it until the commit settles.
            disabled={committing}
            sx={{textTransform: 'none', color: '#8fb0c8'}}
        >
            {t('aiEdit.keepOriginal')}
        </Button>
    );

    const variantPicker = hasVariantPicker && (
        <Box>
            <Typography sx={{fontSize: '0.8rem', color: '#8fb0c8', mb: 0.75}}>
                {t('aiEdit.chooseStyle')}
            </Typography>
            <Box
                data-testid="ai-magic-variant-picker"
                sx={{display: 'flex', flexWrap: 'wrap', gap: 1}}
            >
                {variants.map((variant) => {
                    const active = variant.id === selectedVariantId;
                    return (
                        <Button
                            key={variant.id}
                            data-testid={`ai-magic-variant-${variant.id}`}
                            aria-pressed={active}
                            onClick={() => setSelectedVariantId(variant.id)}
                            startIcon={active ? <SparkleIcon size={14}/> : undefined}
                            sx={{
                                textTransform: 'none',
                                borderRadius: '16px',
                                px: 1.75,
                                color: active ? '#051726' : '#C3F3FD',
                                backgroundColor: active ? '#C3F3FD' : 'transparent',
                                border: '1px solid #C3F3FD',
                                '&:hover': {backgroundColor: active ? '#C3F3FD' : '#ffffff14'},
                            }}
                        >
                            {variant.name}
                        </Button>
                    );
                })}
            </Box>
        </Box>
    );

    return (
        <Box data-testid="ai-magic-stage" sx={{display: 'flex', flexDirection: 'column', gap: 1.5}}>
            {phase !== 'preview' && beforeSrc && (
                <Box sx={PREVIEW_BOX}>
                    <img src={beforeSrc} alt={t('aiEdit.before')} style={IMG_STYLE}/>
                </Box>
            )}

            {phase === 'idle' && (
                <>
                    <Typography sx={{fontSize: '0.9rem', color: '#8fb0c8'}}>
                        {cancelledNotice ? t('aiEdit.cancelledHint') : t('aiEdit.idleHint')}
                    </Typography>
                    {variantPicker}
                    <Button
                        data-testid="ai-magic-start"
                        variant="contained"
                        startIcon={<SparkleIcon/>}
                        onClick={startJob}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 700,
                            borderRadius: '20px',
                            py: 1.2,
                            backgroundColor: '#00E162',
                            '&:hover': {backgroundColor: '#3D9A4A'},
                        }}
                    >
                        {t('aiEdit.start')}
                    </Button>
                    {keepOriginalButton}
                </>
            )}

            {phase === 'running' && (
                <>
                    <LinearProgress
                        data-testid="ai-magic-progress"
                        variant="determinate"
                        value={Math.min(displayProgress, 100)}
                        sx={{borderRadius: 4, height: 8}}
                    />
                    <Typography sx={{fontSize: '0.9rem', color: '#8fb0c8', textAlign: 'center'}}>
                        {t(progressKey)}
                    </Typography>
                    <Button
                        data-testid="ai-magic-cancel"
                        onClick={handleCancel}
                        sx={{textTransform: 'none', color: '#8fb0c8'}}
                    >
                        {t('aiEdit.cancel')}
                    </Button>
                    <Typography sx={{fontSize: '0.75rem', color: '#6f93aa', textAlign: 'center'}}>
                        {t('aiEdit.cancelWarning')}
                    </Typography>
                </>
            )}

            {phase === 'preview' && result && (
                <>
                    {canCrop && compare === 'after' ? (
                        <>
                            {/* Content-sized box (NOT the fixed-height PREVIEW_BOX):
                                ReactCrop sizes to the img, and the img is capped by
                                an explicit maxHeight so a tall result can't spill
                                out below the crop area. */}
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                backgroundColor: '#0e1f2c',
                                borderRadius: '14px',
                                overflow: 'hidden',
                            }}>
                                <ReactCrop
                                    crop={crop}
                                    keepSelection
                                    disabled={committing}
                                    onChange={(c: Crop) => setCrop(c)}
                                    onComplete={(_, pc) => setPercentCrop(pc as PercentCrop)}
                                >
                                    {/* Display img (no crossOrigin so it always renders on
                                        no-CORS buckets); the commit reloads it with CORS. */}
                                    <img
                                        data-testid="ai-magic-compare-image"
                                        src={result.highRes}
                                        alt={t('aiEdit.after')}
                                        style={{
                                            display: 'block',
                                            maxWidth: '100%',
                                            maxHeight: '46vh',
                                            objectFit: 'contain',
                                        }}
                                    />
                                </ReactCrop>
                            </Box>
                            <Typography sx={{fontSize: '0.8rem', color: '#8fb0c8', textAlign: 'center'}}>
                                {t('aiEdit.cropHint')}
                            </Typography>
                        </>
                    ) : (
                        <Box sx={PREVIEW_BOX}>
                            <img
                                data-testid="ai-magic-compare-image"
                                src={compare === 'after' ? result.highRes : beforeSrc}
                                alt={t(compare === 'after' ? 'aiEdit.after' : 'aiEdit.before')}
                                style={IMG_STYLE}
                            />
                        </Box>
                    )}
                    <Box sx={{display: 'flex', gap: 1, justifyContent: 'center'}}>
                        {(['before', 'after'] as CompareSide[]).map((side) => (
                            <Button
                                key={side}
                                data-testid={`ai-magic-compare-${side}`}
                                onClick={() => setCompare(side)}
                                sx={{
                                    textTransform: 'none',
                                    borderRadius: '16px',
                                    px: 2,
                                    color: compare === side ? '#051726' : '#8fb0c8',
                                    backgroundColor: compare === side ? '#C3F3FD' : 'transparent',
                                    border: '1px solid #ffffff30',
                                    '&:hover': {backgroundColor: compare === side ? '#C3F3FD' : '#ffffff10'},
                                }}
                            >
                                {t(side === 'after' ? 'aiEdit.after' : 'aiEdit.before')}
                            </Button>
                        ))}
                    </Box>
                    <Button
                        data-testid="ai-magic-use"
                        variant="contained"
                        onClick={handleUse}
                        // Only blocked while a commit is already uploading. The
                        // crop is free-form and starts at the whole image, so
                        // there is nothing to compute and nothing to wait for.
                        disabled={committing}
                        startIcon={committing ? <CircularProgress size={16} sx={{color: '#051726'}}/> : undefined}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 700,
                            borderRadius: '20px',
                            py: 1.2,
                            backgroundColor: '#00E162',
                            '&:hover': {backgroundColor: '#3D9A4A'},
                        }}
                    >
                        {t('aiEdit.useThis')}
                    </Button>
                    {commitError && (
                        <Typography data-testid="ai-magic-commit-error" sx={{fontSize: '0.85rem', color: '#ffb3ac', textAlign: 'center'}}>
                            {commitError}
                        </Typography>
                    )}
                    {canRetry && (
                        <Button
                            data-testid="ai-magic-retry"
                            startIcon={<SparkleIcon size={16}/>}
                            onClick={handleTryAgain}
                            disabled={committing}
                            sx={{textTransform: 'none', color: '#C3F3FD'}}
                        >
                            {t('aiEdit.tryAgain')}
                        </Button>
                    )}
                    {keepOriginalButton}
                </>
            )}

            {phase === 'error' && (
                <>
                    <Box
                        data-testid="ai-magic-error"
                        sx={{p: 1.5, borderRadius: '10px', backgroundColor: '#ffebee', color: '#c62828'}}
                    >
                        {errorMessage}
                    </Box>
                    {canRetry && !retryBlocked && (
                        <Button
                            data-testid="ai-magic-retry"
                            variant="contained"
                            startIcon={<SparkleIcon size={16}/>}
                            onClick={handleTryAgain}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 700,
                                borderRadius: '20px',
                                backgroundColor: '#00E162',
                                '&:hover': {backgroundColor: '#3D9A4A'},
                            }}
                        >
                            {t('aiEdit.tryAgain')}
                        </Button>
                    )}
                    {keepOriginalButton}
                </>
            )}
        </Box>
    );
};

export default AiMagicStage;
